import Component from '@glimmer/component';
import { service } from '@ember/service';
import AllFundsJournalSlimModel from 'donor-elf-web/models/all-funds/journal-slim';
import Store from '@ember-data/store';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import fuzzysort from 'fuzzysort';

interface ComponentArgs {
  existingSlimJournals: AllFundsJournalSlimModel[];
  onSave: (slimJournals: AllFundsJournalSlimModel[]) => void;
  onCancel: () => void;
}

class JournalItem {
  constructor(
    public slimJournal: AllFundsJournalSlimModel,
    selected: boolean,
  ) {
    this.selected = selected;
    this.label = `${slimJournal.fundName}: ${slimJournal.label}`;
    this.labelPrepared = fuzzysort.prepare(this.label);
  }

  @tracked selected = false;
  label: string;
  labelPrepared: Fuzzysort.Prepared;
  @tracked fuzzyResult?: Fuzzysort.KeyResult<JournalItem>;
}

export default class JournalGroupEditJournalsComponent extends Component<ComponentArgs> {
  @service declare store: Store;

  @tracked searchVal = '';
  @tracked journalItems: JournalItem[] = [];
  @tracked loading = true;

  @action
  templateDidInsert() {
    this.load();
  }

  async load() {
    this.loading = true;
    let allSlimJournals = (
      await this.store.query('all-funds/journal-slim', {})
    ).slice();
    let existinSlimJournalIds: { [id: string]: boolean } = {};
    for (let slimJournal of this.args.existingSlimJournals) {
      existinSlimJournalIds[slimJournal.id] = true;
    }
    this.journalItems = sortByProperties(
      allSlimJournals.map((slimJournal: AllFundsJournalSlimModel) => {
        return new JournalItem(
          slimJournal,
          existinSlimJournalIds[slimJournal.id],
        );
      }),
      [
        'selected:desc',
        'slimJournal.fundName:asc',
        'slimJournal.startDate:desc',
      ],
    );
    this.loading = false;
  }

  get filteredJournalItems() {
    if ((this.searchVal || '').length < 3) {
      for (let journalItem of this.journalItems) {
        journalItem.fuzzyResult = undefined;
      }
      return this.journalItems;
    } else {
      return fuzzysort
        .go(this.searchVal, this.journalItems, {
          threshold: -10000,
          key: ['labelPrepared'],
        })
        .map((fuzzyResult: Fuzzysort.KeyResult<JournalItem>) => {
          fuzzyResult.obj.fuzzyResult = fuzzyResult;
          return fuzzyResult.obj;
        });
    }
  }

  @action
  handleSearchInput(e: any) {
    this.searchVal = e.target.value;
  }

  @action
  toggleSelected(journalItem: JournalItem) {
    journalItem.selected = !journalItem.selected;
  }

  @action
  save() {
    this.args.onSave(
      this.journalItems
        .filter((journalItem) => journalItem.selected)
        .map((journalItem) => journalItem.slimJournal),
    );
  }
}
